import { useMemo } from "react";
import { type UserStory } from "@/lib/projectStore";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Flame } from "lucide-react";

interface Props {
  stories: UserStory[];
  projectStartDate: string;
  sprintLengthWeeks: number;
}

export default function BurndownChart({ stories, projectStartDate, sprintLengthWeeks }: Props) {
  const { chartData, totalPoints, idealLine } = useMemo(() => {
    const total = stories.reduce((a, s) => a + (s.storyPoints || 0), 0);
    const startMs = new Date(projectStartDate).getTime();
    const sprintMs = sprintLengthWeeks * 7 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    const totalSprints = Math.max(1, Math.ceil((now - startMs) / sprintMs));
    const sprintCount = Math.min(totalSprints, 12);

    const data: { sprint: string; remaining: number; ideal: number }[] = [];
    let cumDone = 0;

    for (let i = 0; i <= sprintCount; i++) {
      const sprintStart = startMs + (i > 0 ? (i - 1) * sprintMs : 0);
      const sprintEnd = startMs + i * sprintMs;

      if (i > 0) {
        stories.forEach((s) => {
          if (s.status === "done" && s.dueDate) {
            const end = new Date(s.dueDate).getTime();
            if (end >= sprintStart && end < sprintEnd) {
              cumDone += s.storyPoints || 0;
            }
          }
        });
      }

      const idealPerSprint = total / sprintCount;
      data.push({
        sprint: i === 0 ? "Start" : `S${i}`,
        remaining: Math.max(0, total - cumDone),
        ideal: Math.max(0, Math.round((total - idealPerSprint * i) * 10) / 10),
      });
    }

    return { chartData: data, totalPoints: total, idealLine: true };
  }, [stories, projectStartDate, sprintLengthWeeks]);

  const hasWork = totalPoints > 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-display flex items-center gap-2">
          <Flame className="h-4 w-4 text-accent" />
          Sprint Burndown
        </CardTitle>
        <CardDescription className="text-xs">
          Remaining story points over time ({sprintLengthWeeks}w sprints) — {totalPoints} total pts
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!hasWork ? (
          <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
            <div className="text-center space-y-2">
              <Flame className="h-8 w-8 mx-auto opacity-40" />
              <p>No stories with points yet.</p>
              <p className="text-xs">Add story points to see the burndown chart.</p>
            </div>
          </div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                <defs>
                  <linearGradient id="burnGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" />
                <XAxis
                  dataKey="sprint"
                  tick={{ fontSize: 11 }}
                  className="fill-muted-foreground"
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  className="fill-muted-foreground"
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                  formatter={(value: number, name: string) => [
                    `${value} pts`,
                    name === "remaining" ? "Remaining" : "Ideal",
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="ideal"
                  stroke="hsl(var(--accent))"
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  fill="none"
                  dot={false}
                />
                <Area
                  type="monotone"
                  dataKey="remaining"
                  stroke="hsl(var(--chart-1))"
                  strokeWidth={2}
                  fill="url(#burnGrad)"
                  dot={{ r: 3, fill: "hsl(var(--chart-1))" }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
